import i18n from "~/i18n/i18n";

export interface ReportNameRef {
	id: string;
	name: string;
}

export interface TemplateNameRef extends ReportNameRef {
	isSystem: boolean;
}

export interface ReportQueryState {
	isError: boolean;
	isLoading: boolean;
}

export type ReportOpenResolution =
	| { kind: "match"; reachedDestination: string; reportId: string }
	| { kind: "templateMatch"; reachedDestination: string; templateId: string }
	| { kind: "ambiguous" }
	| { kind: "notFound" };

export type ReportOpenPlan = ReportOpenResolution | { kind: "loadFailure" } | { kind: "pending" };

export type ReportOpenMiss = { kind: "ambiguous" } | { kind: "loadFailure" } | { kind: "notFound" };

export type ReportNavStep = "deferToConsumer" | "failViewport" | "navigateToReports";

export const REPORTS_LOAD_FAILURE_MESSAGE = "Couldn't load your reports — please try again.";

export const REPORTS_VIEWPORT_MESSAGE =
	"The report builder opens on a larger screen — switch to a desktop or tablet to open a report.";

const normalizeName = (value: string): string => value.trim().toLowerCase();

const describeReachedReportBuilder = (reportName: string): string => `the "${reportName}" report in the report builder`;

const describeReachedTemplatePreview = (templateName: string): string => `the "${templateName}" report`;

const resolveReportByName = (
	reports: ReportNameRef[],
	reportName: string,
	templates: TemplateNameRef[] = []
): ReportOpenResolution => {
	const target = normalizeName(reportName);
	if (!target) return { kind: "notFound" };
	const matches = reports.filter((report) => normalizeName(report.name) === target);
	if (matches.length === 0) {
		const templateMatches = templates.filter(
			(template) => template.isSystem && normalizeName(template.name) === target
		);
		if (templateMatches.length === 0) return { kind: "notFound" };
		if (templateMatches.length > 1) return { kind: "ambiguous" };
		return {
			kind: "templateMatch",
			reachedDestination: describeReachedTemplatePreview(templateMatches[0].name),
			templateId: templateMatches[0].id
		};
	}
	if (matches.length > 1) return { kind: "ambiguous" };
	return { kind: "match", reachedDestination: describeReachedReportBuilder(matches[0].name), reportId: matches[0].id };
};

const planReportOpen = (
	reportsState: ReportQueryState,
	reports: ReportNameRef[],
	templatesState: ReportQueryState,
	templates: TemplateNameRef[],
	reportName: string
): ReportOpenPlan => {
	const reportsReady = !reportsState.isLoading && !reportsState.isError;
	if (reportsReady) {
		const savedResolution = resolveReportByName(reports, reportName);
		if (savedResolution.kind === "match" || savedResolution.kind === "ambiguous") return savedResolution;
	}
	if (reportsState.isLoading || templatesState.isLoading) return { kind: "pending" };
	if (reportsState.isError || templatesState.isError) return { kind: "loadFailure" };
	return resolveReportByName(reports, reportName, templates);
};

const buildReportOpenMiss = (miss: ReportOpenMiss, reportName: string): string => {
	switch (miss.kind) {
		case "loadFailure":
			return REPORTS_LOAD_FAILURE_MESSAGE;
		case "ambiguous":
			return i18n.t("fernieReportOpen.ambiguous", { ns: "common", reportName });
		case "notFound":
			return i18n.t("fernieReportOpen.notFound", { ns: "common", reportName });
	}
};

const planReportNavigation = (pathname: string, reportsPath: string, isWideScreen: boolean): ReportNavStep => {
	if (pathname !== reportsPath) return "navigateToReports";
	return isWideScreen ? "deferToConsumer" : "failViewport";
};

export { buildReportOpenMiss, describeReachedReportBuilder, planReportNavigation, planReportOpen };
export default resolveReportByName;
